<template>
  <div>
    <a class="h-10 px-4 mr-4 border border-gray-600 text-gray-600 flex items-center hover:bg-gray-600 hover:text-white cursor-pointer rounded" @click="showing = true">Add Optional Accessory</a>
    <card-modal :showing="showing" class="px-4 py-2" @close="showing = false">
      <div class="flex">
        <h2 class="flex-auto text-3xl font-bold">Add Optional Accessory</h2>
        <div class="flex items-center">
          <div class="flex w-full bg-white shadow">
            <input v-model="search" class="relative w-full px-6 py-2 focus:shadow-outline" autocomplete="off" type="text" name="search" placeholder="Search Accessory" />
          </div>
          <button class="ml-3 text-sm text-gray-500 hover:text-gray-700 focus:text-blue-500" type="button" @click="search = ''">Reset</button>
        </div>
      </div>
      <div class="mt-6">
        <div class="pr-2 overflow-y-scroll max-h-96">
          <table class="w-full">
            <thead>
              <tr class="text-lg font-bold text-left text-center">
                <th class="pt-2 pr-2">Select</th>
                <th class="pt-2 pr-4">Part Number</th>
                <th class="pt-2 pr-4">Description</th>
                <th class="pt-2 pr-2">Qty</th>
                <th class="pt-2 pr-4">Image</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(accessory, index) in filteredAccessories" :key="index"
                class="hover:bg-gray-200 focus-within:bg-gray-300 relative group"
                :class="index % 2 == 0 ? '' : 'bg-gray-100'"
              >
                <td class="text-center">
                  <input v-model="selected" type="checkbox" :value="accessory" @change="onAccessoryToggle(accessory, $event)" />
                </td>
                <td class="p-2">
                  <div class="flex items-center gap-2">
                    {{ accessory.part_number }}
                    <!-- Tooltip icon -->
                    <div v-if="accessory.tooltip" class="relative group">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400 cursor-help"
                        fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <!-- Tooltip content -->
                      <div class="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                        <div class="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap" style="min-width: 150px;">
                          {{ accessory.tooltip }}
                          <svg class="absolute text-gray-900 h-2 w-full left-0 top-full" x="0px" y="0px" viewBox="0 0 255 255"
                            xml:space="preserve">
                            <polygon class="fill-current" points="0,0 127.5,127.5 255,0" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="">{{ accessory.description || 'Accessory' }}</td>
                <td class="text-center p-2">
                  <div v-if="isSelected(accessory)" class="flex flex-col items-center justify-center">
                    <input type="number" min="1" :value="getQuantity(accessory.id)"
                      @input="updateQuantity(accessory.id, $event.target.value)"
                      @change="updateQuantity(accessory.id, $event.target.value)"
                      class="w-16 h-8 border border-gray-300 text-center text-sm rounded" />
                    <span v-if="hasAutoQuantity(accessory)" class="text-xs text-gray-400 mt-0.5">auto-filled</span>
                  </div>
                  <span v-else class="text-gray-400 text-xs">-</span>
                </td>
                <td class="text-center p-2">
                  <img v-if="accessory.img_path"
                    :src="getImageUrl(accessory.img_path)"
                    :alt="accessory.part_number"
                    class="max-h-12 object-contain mx-auto" />
                  <span v-else class="text-gray-400 text-xs">No image</span>
                </td>
              </tr>
              <tr v-if="accessories.length == 0">
                <td class="px-6 py-4 text-xl text-center border-t" colspan="6">No optional accessories available.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="flex items-center justify-between mt-6">
          <div class="">Total <span class="p-1 bg-gray-200 rounded-full">{{ filteredAccessories.length }}</span></div>
          <button class="px-6 btn-blue" @click.stop="add()">Add</button>
        </div>
      </div>
    </card-modal>
  </div>
</template>

<script>
import CardModal from '@/Shared/CardModal.vue'

export default {
  components: {
    CardModal,
  },
  props:{
    accessories: {
      type: Array,
      default: () => []
    },
    // Live channel count of the run being edited, used to seed the default quantity for
    // accessories with a "per channel" calculation_formula (e.g. T1460-REB: 'channels').
    channelCount: {
      type: Number,
      default: 1
    },
  },
  created() {
    console.log('AddOptionalAccessory created with accessories:', this.accessories);
  },
  emits:['input'],
  data() {
    return {
      search  : '',
      selected: [],
      quantities: {}, // Track quantities for selected accessories
      showing : false,
    }
  },
  computed: {
    filteredAccessories() {
      return this.accessories.filter(el => {
        let search  = this.search.toLowerCase()

        let part_number = (el.part_number || '').toLowerCase()
        let description = (el.description || '').toLowerCase()

        return part_number.indexOf(search) > -1 || description.indexOf(search) > -1
      })
    },
  },
  methods: {
    add() {
      // Convert accessories to parts format for the table. Quantity comes straight from
      // this.quantities: onAccessoryToggle() already seeded it (auto-calculated for
      // formula-driven accessories, 1 otherwise), and updateQuantity() reflects any manual
      // edit since — so it must not be recalculated here, or a manual override would be
      // silently discarded.
      const partsToAdd = this.selected.map(accessory => {
        const quantity = this.quantities[accessory.id] || 1;

        const partData = {
          id: accessory.id,
          type: 'accessory',
          part_number: accessory.part_number,
          description: accessory.description || 'Accessory',
          length: 0, // Accessories typically don't have length
          bo_size: null, // Add missing field for outlet type detection
          outlet_size: null, // Add missing field for outlet type detection
          quantity: quantity,
        };

        return partData;
      });

      this.$emit('input', partsToAdd)
      this.showing  = false
      this.selected = []
      this.quantities = {}
    },

    // Calculate automatic quantity based on formula from DB
    calculateAutoQuantity(accessory) {
      if (!accessory.calculation_formula) {
        return 1; // Default quantity if no formula
      }

      try {
        // channels/channelCount reflect the run's actual channel count (see the
        // channelCount prop) so a "per channel" formula like T1460-REB's ('channels')
        // scales correctly. length/grateLength have no equivalent live value available
        // for a custom run (no computed run length), so they stay a fixed default.
        const channels = this.channelCount || 1;
        const length = 1; // Default
        const channelCount = channels;
        const grateLength = length;

        // Evaluate the formula
        // Using Function constructor for better security than eval
        const formula = new Function('channels', 'length', 'channelCount', 'grateLength', 'Math',
          `return ${accessory.calculation_formula}`);

        const result = formula(channels, length, channelCount, grateLength, Math);
        return Math.max(1, Math.round(result)); // Ensure at least 1
      } catch (error) {
        console.error('Error calculating quantity for', accessory.part_number, ':', error);
        return 1; // Default to 1 if formula fails
      }
    },

    onAccessoryToggle(accessory, event) {
      console.log('=== ACCESSORY TOGGLE ===');
      console.log('Accessory:', accessory.part_number);
      console.log('Checked:', event.target.checked);
      console.log('Before toggle - this.quantities:', this.quantities);

      if (event.target.checked) {
        // Initialize quantity when accessory is selected (only if not already set):
        // auto-calculated for a formula-driven accessory (e.g. one per channel), else 1.
        // Still editable afterwards via updateQuantity().
        if (!this.quantities[accessory.id]) {
          this.quantities[accessory.id] = this.hasAutoQuantity(accessory)
            ? this.calculateAutoQuantity(accessory)
            : 1;
          console.log('Initialized quantity to', this.quantities[accessory.id], 'for:', accessory.part_number);
        } else {
          console.log('Quantity already exists:', this.quantities[accessory.id]);
        }
      } else {
        // Remove quantity when accessory is deselected
        delete this.quantities[accessory.id];
        console.log('Removed quantity for:', accessory.part_number);
      }

      console.log('After toggle - this.quantities:', this.quantities);
      console.log('========================');
    },

    isSelected(accessory) {
      return this.selected.some(item => item.id === accessory.id)
    },

    hasAutoQuantity(accessory) {
      return !!accessory.calculation_formula
    },

    getQuantity(accessoryId) {
      return this.quantities[accessoryId] || 1
    },

    updateQuantity(accessoryId, quantity) {
      const parsedQuantity = parseInt(quantity, 10) || 1;
      console.log('=== UPDATE QUANTITY ===');
      console.log('Accessory ID:', accessoryId);
      console.log('Raw quantity:', quantity);
      console.log('Parsed quantity:', parsedQuantity);
      console.log('Before update - this.quantities:', this.quantities);

      this.quantities[accessoryId] = parsedQuantity;

      console.log('After update - this.quantities:', this.quantities);
      console.log('=====================');
    },

    getImageUrl(imgPath) {
      if (!imgPath) return null;

      // Check if it's already a full URL
      if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
        return imgPath;
      }

      // Add the appropriate prefix and query parameters
      if (imgPath.startsWith('/')) {
        // If path starts with a slash, don't add another one
        return `${window.location.origin}${imgPath}?w=120&fit=contain`;
      } else {
        // If it's just a relative path like "accessories/...", add /img/ prefix
        return `${window.location.origin}/img/${imgPath}?w=120&fit=contain`;
      }
    },
  },
}
</script>
